import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { form, required } from '@angular/forms/signals';
import { UserAvatarFileDto } from '@workspace/data-types';
import { AbstractSignalFormComponent } from '../../../../abstract';
import { AvatarFileInputAuraComponent } from '../../../../inputs/file/file-input.component';
import { FilePreview } from '../../../../inputs/file/preview/file-input-preview.component';

export interface PersonAvatarModel {
  file: File | UserAvatarFileDto | null;
}

@Component({
  selector: 'aura-person-avatar-form',
  imports: [AvatarFileInputAuraComponent],
  templateUrl: './person-identity-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuraPersonAvatarFormComponent extends AbstractSignalFormComponent<
  File | UserAvatarFileDto,
  PersonAvatarModel
> {
  @ViewChild(AvatarFileInputAuraComponent)
  fileInputCmp?: AvatarFileInputAuraComponent;

  fileControl = new FormControl<File | UserAvatarFileDto | null>(null);
  avatarPreview = signal<FilePreview | null>(null);
  placeholder = input.required<string>();

  override model: WritableSignal<PersonAvatarModel> = signal<PersonAvatarModel>({
    file: null,
  });

  override formTree = form(this.model, (b) => {
    required(b.file, { when: () => this.isRequired('avatar') || this.isRequired('file') });
  });

  constructor() {
    super();
    effect(() => {
      const fileValue = this.model().file;
      if (this.fileControl.value !== fileValue) {
        this.fileControl.setValue(fileValue, { emitEvent: false });
      }
      this.setPreview(fileValue);
    });
  }

  onAvatarSelected(file: File | null) {
    this.model.set({ file });
  }

  removeAvatar() {
    this.model.set({ file: null });
    this.fileControl.setValue(null);
    this.avatarPreview.set(null);
    this.fileInputCmp?.removeFile();
  }

  private setPreview(file: File | UserAvatarFileDto | null) {
    if (!file) {
      this.avatarPreview.set(null);
      return;
    }

    if ('url' in file) {
      this.avatarPreview.set({
        url: file.url,
        size: file.size,
        name: file.filename,
      });
      return;
    }

    if (file instanceof File) {
      const reader = new FileReader();
      reader.onload = () => {
        this.avatarPreview.set({
          url: reader.result as string,
          size: file.size,
          name: file.name,
        });
      };
      reader.readAsDataURL(file);
      return;
    }

    this.avatarPreview.set(null);
  }

  protected override buildModelFromDto(dto: File | UserAvatarFileDto): PersonAvatarModel {
    return {
      file: dto ?? null,
    };
  }

  protected override buildDtoFromModel(model: PersonAvatarModel): File | UserAvatarFileDto {
    return model.file as File | UserAvatarFileDto;
  }
}
